
import { GlassCard } from "@/components/ui/glass-card";
import { Loader2, MonitorSmartphone, Activity, Calendar, Shield, TrendingUp, TrendingDown } from "lucide-react";

interface DeviceStatusCardsProps {
  counts: {
    active: number;
    inactive: number;
    pending: number;
    total: number;
  };
  isLoading: boolean;
  statusFilter: string | null;
  onStatusClick: (status: string | null) => void;
}

export const DeviceStatusCards = ({
  counts,
  isLoading,
  statusFilter,
  onStatusClick,
  mode = "full",
}: DeviceStatusCardsProps & { mode?: "full" | "compact" | "hidden" }) => {
  if (mode === "hidden") return null;
  // More realistic data for device change percentages based on our mock data
  const changes = {
    total: 15, // 15% growth in total devices in last 30 days
    active: 8, // 8% growth in active/healthy devices
    inactive: -20, // 20% decrease in non-compliant devices (improvement)
    pending: 12 // 12% increase in pending devices
  };

  const isCompact = mode === "compact";

  return (
    <div className={`grid grid-cols-1 md:grid-cols-4 gap-${isCompact ? '3' : '6'} ${isCompact ? 'mb-4' : 'mb-8'} transition-all duration-500 ease-in-out`}>
      <GlassCard 
        variant="subtle"
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${
          statusFilter === null ? 'ring-2 ring-black dark:ring-white' : ''
        }`}
        onClick={() => onStatusClick(null)}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>All Devices</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <MonitorSmartphone className="h-5 w-5 text-slate-600 dark:text-slate-400" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-slate-600" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-slate-800 dark:text-white transition-all duration-500 ease-in-out`}>{counts.total}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Enrolled endpoints</p>}
              {!isCompact && <div className="flex items-center gap-1 mt-2 text-sm text-blue-600 dark:text-blue-500">
                {changes.total > 0 ? (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    <span>{changes.total}% growth in last 30 days</span>
                  </>
                ) : changes.total < 0 ? (
                  <>
                    <TrendingDown className="h-4 w-4" />
                    <span>{Math.abs(changes.total)}% decrease in last 30 days</span>
                  </>
                ) : (
                  <span>No change in last 30 days</span>
                )}
              </div>}
            </div>
          </>
        )}
      </GlassCard>
      
      <GlassCard 
        variant="subtle"
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${
          statusFilter === 'active' ? 'ring-2 ring-emerald-500' : ''
        }`}
        onClick={() => onStatusClick('active')}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>Healthy</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <Activity className="h-5 w-5 text-emerald-600 dark:text-emerald-500" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-emerald-600 dark:text-emerald-500 transition-all duration-500 ease-in-out`}>{counts.active}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Devices in compliance</p>}
              {!isCompact && <div className="flex items-center gap-1 mt-2 text-sm text-emerald-600">
                <TrendingUp className="h-4 w-4" />
                <span>{Math.round((counts.active / counts.total) * 100)}% compliance rate</span>
              </div>}
              {!isCompact && <div className="flex items-center gap-1 mt-1 text-sm text-emerald-600">
                {changes.active > 0 ? (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    <span>{changes.active}% more healthy devices in 30 days</span>
                  </>
                ) : changes.active < 0 ? (
                  <>
                    <TrendingDown className="h-4 w-4" />
                    <span>{Math.abs(changes.active)}% fewer healthy devices in 30 days</span>
                  </>
                ) : (
                  <span>Stable healthy device count</span>
                )}
              </div>}
            </div>
          </>
        )}
      </GlassCard>
      
      <GlassCard 
        variant="subtle"
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${
          statusFilter === 'pending' ? 'ring-2 ring-amber-500' : ''
        }`}
        onClick={() => onStatusClick('pending')}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>Pending Actions</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <Calendar className="h-5 w-5 text-amber-600 dark:text-amber-500" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-amber-600" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-amber-600 dark:text-amber-500 transition-all duration-500 ease-in-out`}>{counts.pending}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Devices awaiting setup</p>}
              {!isCompact && counts.pending > 0 && (
                <div className="flex items-center gap-1 mt-2 text-sm text-amber-600">
                  <span>Requires admin attention</span>
                </div>
              )}
              {!isCompact && <div className="flex items-center gap-1 mt-1 text-sm text-amber-600">
                {changes.pending > 0 ? (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    <span>{changes.pending}% new enrollments in 30 days</span>
                  </>
                ) : changes.pending < 0 ? (
                  <>
                    <TrendingDown className="h-4 w-4" />
                    <span>{Math.abs(changes.pending)}% fewer pending in 30 days</span>
                  </>
                ) : (
                  <span>No new enrollment trend</span>
                )}
              </div>}
            </div>
          </>
        )}
      </GlassCard>
      
      <GlassCard 
        variant="subtle"
        className={`${isCompact ? 'p-3' : 'p-6'} cursor-pointer ${
          statusFilter === 'inactive' ? 'ring-2 ring-rose-500' : ''
        }`}
        onClick={() => onStatusClick('inactive')}
      >
        <div className={`flex justify-between items-start ${isCompact ? 'mb-1' : 'mb-2'} transition-all duration-500 ease-in-out`}>
          <h3 className={`${isCompact ? 'text-sm' : 'text-lg'} font-semibold text-slate-900 dark:text-white transition-all duration-500 ease-in-out`}>Non-Compliant</h3>
          <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 scale-0' : 'opacity-100 scale-100'}`}>
            {!isCompact && <Shield className="h-5 w-5 text-rose-600 dark:text-rose-500" />}
          </div>
        </div>
        {isLoading ? (
          <Loader2 className="h-6 w-6 animate-spin text-rose-600" />
        ) : (
          <>
            <p className={`${isCompact ? 'text-xl' : 'text-3xl'} font-bold text-rose-600 dark:text-rose-500 transition-all duration-500 ease-in-out`}>{counts.inactive}</p>
            <div className={`transition-all duration-500 ease-in-out ${isCompact ? 'opacity-0 max-h-0 overflow-hidden' : 'opacity-100 max-h-20'}`}>
              {!isCompact && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Devices requiring attention</p>}
              {!isCompact && <div className="flex items-center gap-1 mt-2 text-sm text-rose-600">
                <TrendingDown className="h-4 w-4" />
                <span>Security policy violations</span>
              </div>}
              {!isCompact && <div className="flex items-center gap-1 mt-1 text-sm text-rose-600">
                {changes.inactive > 0 ? (
                  <>
                    <TrendingUp className="h-4 w-4" />
                    <span>{changes.inactive}% increase in violations</span>
                  </>
                ) : changes.inactive < 0 ? (
                  <>
                    <TrendingDown className="h-4 w-4" />
                    <span>{Math.abs(changes.inactive)}% fewer violations in 30 days</span>
                  </>
                ) : (
                  <span>Stable compliance violations</span>
                )}
              </div>}
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
};
